import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useLeague } from './LeagueContext';

const MessagesContext = createContext({});

export const useMessages = () => {
  const context = useContext(MessagesContext);
  if (!context) throw new Error('useMessages must be used within MessagesProvider');
  return context;
};

export const MessagesProvider = ({ children }) => {
  const { user } = useAuth();
  const { activeLeague, leagueMembers } = useLeague();
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch all conversations for the current user in the active league
  const fetchConversations = useCallback(async () => {
    if (!supabase || !user || !activeLeague) {
      setConversations([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('league_id', activeLeague.id)
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Group by conversation partner
      const convMap = {};
      (data || []).forEach((msg) => {
        const partnerId = msg.sender_id === user.id ? msg.recipient_id : msg.sender_id;
        if (!convMap[partnerId]) {
          convMap[partnerId] = {
            partnerId,
            partnerName: leagueMembers.find((m) => m.userId === partnerId)?.name || 'Unknown User',
            lastMessage: msg,
            unreadCount: 0,
            messages: [],
          };
        }
        convMap[partnerId].messages.push(msg);
        if (msg.recipient_id === user.id && !msg.read) {
          convMap[partnerId].unreadCount++;
        }
      });

      const convList = Object.values(convMap).sort(
        (a, b) => new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
      );

      setConversations(convList);
      setUnreadCount(convList.reduce((sum, c) => sum + c.unreadCount, 0));
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [user, activeLeague, leagueMembers]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Set up realtime subscription for new messages
  useEffect(() => {
    if (!supabase || !user || !activeLeague) return;

    const channel = supabase
      .channel(`messages-realtime-${activeLeague.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `league_id=eq.${activeLeague.id}`,
        },
        (payload) => {
          const msg = payload.new;
          if (msg.sender_id === user.id || msg.recipient_id === user.id) {
            fetchConversations();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, user, activeLeague, fetchConversations]);

  // Get messages for a specific conversation
  const getConversation = useCallback(async (partnerId) => {
    if (!supabase || !user || !activeLeague) return [];

    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('league_id', activeLeague.id)
      .or(`and(sender_id.eq.${user.id},recipient_id.eq.${partnerId}),and(sender_id.eq.${partnerId},recipient_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Failed to get conversation:', error);
      return [];
    }
    return data || [];
  }, [user, activeLeague]);

  // Send a message
  const sendMessage = useCallback(async (recipientId, content) => {
    if (!supabase || !user || !activeLeague) throw new Error('Not authenticated');
    if (!content.trim()) throw new Error('Message cannot be empty');

    const { error } = await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        recipient_id: recipientId,
        league_id: activeLeague.id,
        content: content.trim(),
      });
    if (error) throw error;

    await fetchConversations();
  }, [user, activeLeague, fetchConversations]);

  // Mark messages from a sender as read
  const markAsRead = useCallback(async (senderId) => {
    if (!supabase || !user || !activeLeague) return;

    const { error } = await supabase
      .from('messages')
      .update({ read: true })
      .eq('sender_id', senderId)
      .eq('recipient_id', user.id)
      .eq('league_id', activeLeague.id)
      .eq('read', false);

    if (error) {
      console.error('Failed to mark as read:', error);
      return;
    }
    await fetchConversations();
  }, [user, activeLeague, fetchConversations]);

  return (
    <MessagesContext.Provider value={{
      conversations,
      unreadCount,
      loading,
      fetchConversations,
      getConversation,
      sendMessage,
      markAsRead,
    }}>
      {children}
    </MessagesContext.Provider>
  );
};

export default MessagesContext;
